import { useState } from 'react'
import { Button, Card, Table, Modal, Form, Badge } from 'react-bootstrap'
import { 
  useGetAllPromptsQuery, 
  useCreatePromptMutation, 
  useUpdatePromptMutation, 
  useDeletePromptMutation,
  Prompt,
  PromptType
} from '../../services/prompts.service'

function PromptsManagement(): JSX.Element {
  const { data: prompts, isLoading, refetch } = useGetAllPromptsQuery({})
  const [createPrompt] = useCreatePromptMutation()
  const [updatePrompt] = useUpdatePromptMutation()
  const [deletePrompt] = useDeletePromptMutation()
  
  const [showModal, setShowModal] = useState(false)
  const [currentPrompt, setCurrentPrompt] = useState<Partial<Prompt>>({})
  const [isEditing, setIsEditing] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [promptToDelete, setPromptToDelete] = useState<number | null>(null)

  const handleCloseModal = () => {
    setShowModal(false)
    setCurrentPrompt({})
    setIsEditing(false)
  }

  const handleShowModal = (editing = false, prompt: Partial<Prompt> = {}) => {
    setIsEditing(editing)
    setCurrentPrompt(prompt)
    setShowModal(true)
  }

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false)
    setPromptToDelete(null)
  }

  const handleShowDeleteModal = (id: number) => {
    setPromptToDelete(id)
    setShowDeleteModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      if (isEditing && currentPrompt.id) {
        await updatePrompt({
          id: currentPrompt.id,
          prompt: {
            name: currentPrompt.name,
            note: currentPrompt.note,
            content: currentPrompt.content,
            promptType: currentPrompt.promptType,
            status: currentPrompt.status
          }
        }).unwrap()
      } else {
        await createPrompt({
          name: currentPrompt.name,
          note: currentPrompt.note,
          content: currentPrompt.content,
          promptType: currentPrompt.promptType || PromptType.PROMPT_INSPIRATION,
          status: currentPrompt.status || 0
        }).unwrap()
      }
      
      handleCloseModal()
      refetch()
    } catch (error) {
      console.error('Error saving prompt:', error)
    }
  }

  const handleDelete = async () => {
    if (promptToDelete) {
      try {
        await deletePrompt(promptToDelete).unwrap()
        handleCloseDeleteModal()
        refetch()
      } catch (error) {
        console.error('Error deleting prompt:', error)
      }
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setCurrentPrompt({
      ...currentPrompt,
      [name]: name === 'status' ? parseInt(value, 10) : value
    })
  }

  const getStatusBadge = (status: number) => {
    switch (status) {
      case 0:
        return <Badge bg="secondary">Draft</Badge>
      case 1:
        return <Badge bg="success">Active</Badge>
      case 2:
        return <Badge bg="danger">Inactive</Badge>
      default:
        return <Badge bg="secondary">Unknown</Badge>
    }
  }

  if (isLoading) {
    return <div>Loading...</div>
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Prompts Management</h2>
        <Button variant="primary" onClick={() => handleShowModal(false)}>
          Add New Prompt
        </Button>
      </div>

      <Card>
        <Card.Body>
          <Table responsive striped hover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Note</th>
                <th>Type</th>
                <th>Status</th>
                <th>Created</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {prompts && (prompts as any).length > 0 ? (
                (prompts as any).map((prompt: any) => (
                  <tr key={prompt.id}>
                    <td>{prompt.name}</td>
                    <td>{prompt.note}</td>
                    <td><Badge bg={prompt.promptType === PromptType.PROMPT_SYSTEM ? 'info' : 'warning'}>{prompt.promptType === PromptType.PROMPT_SYSTEM ? 'System' : 'Inspiration'}</Badge></td>
                    <td>{getStatusBadge(prompt.status)}</td>
                    <td>{new Date(prompt.createdAt).toLocaleDateString()}</td>
                    <td>{new Date(prompt.updatedAt).toLocaleDateString()}</td>
                    <td>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-2"
                        onClick={() => handleShowModal(true, prompt)}
                      >
                        Edit
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleShowDeleteModal(prompt.id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center">No prompts found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Add/Edit Prompt Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? 'Edit Prompt' : 'Add New Prompt'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={currentPrompt.name || ''}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Note</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="note"
                value={currentPrompt.note || ''}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Content</Form.Label>
              <Form.Control
                as="textarea"
                rows={10}
                name="content"
                value={currentPrompt.content || ''}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Prompt Type</Form.Label>
              <Form.Select
                name="promptType"
                value={currentPrompt.promptType || PromptType.PROMPT_INSPIRATION}
                onChange={handleInputChange}
              >
                <option value={PromptType.PROMPT_INSPIRATION}>Inspiration Prompt</option>
                <option value={PromptType.PROMPT_SYSTEM}>System Prompt</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="status"
                value={currentPrompt.status?.toString() || '0'}
                onChange={handleInputChange}
              >
                <option value="0">Draft</option>
                <option value="1">Active</option>
                <option value="2">Inactive</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {isEditing ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this prompt? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default PromptsManagement
